/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormContext, useFieldArray } from "react-hook-form";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";

import LocalLoader from "../../../../Loaders/Local";
import RecordRenderer from "../RecordRenderer";

import { useUpdateApplicationFormMutation } from "../../../../../api/applications.api.slice";
import { updateDatagridRecords } from "../../../context/formSlice";
import { showNotification } from "../../../../../features/core/slices/notification.slice";

import useDatagridState from "../../../hooks/useDatagridState";
import useUpdatePanelHeight from "../../../hooks/useUpdatePanelHeight";
import useDgRenderState from "../../../hooks/useDgRenderState";
import useScrollPosition from "../../../../../hooks/useScrollPosition";
import { StyledBox } from "../styles";

function DynamicView(props) {
  const dispatch = useDispatch();
  const formConfig = useSelector((state) => state.formProps.formConfig);
  const applicationStatus = useSelector(
    (state) => state.formProps.applicationData?.status
  );
  const { gridItem, formData, dgConfigRef, isCategorizedView } = props;
  const { control } = useFormContext();
  const {
    fields: records,
    append,
    remove,
    update,
    replace,
  } = useFieldArray({
    control,
    name: gridItem.key,
  });
  const [updateForm, { isLoading }] = useUpdateApplicationFormMutation();
  const { updateDgState } = useDatagridState();
  const { getDgRenderInfo, updateDgRenderState } = useDgRenderState();
  const { updateHeight } = useUpdatePanelHeight();
  const [captureCurrentPosition, scrollToCapturedPosition] =
    useScrollPosition();

  const allowBtnAction = useRef(true);
  const showBtnForCategorizedView = ["Open", "Rejected"].includes(
    applicationStatus
  );

  const appendRecord = () => {
    append({ _id: Date.now() });
    allowBtnAction.current = false;
  };
  const removeRecord = (index) => {
    remove(index);
    allowBtnAction.current = true;
  };
  const updateRecord = (index, data) => {
    update(index, data);
    allowBtnAction.current = true;
  };
  useEffect(() => {
    const datagridList = formData[gridItem.key];
    const dgRenderInfo = getDgRenderInfo();
    const isEmptyList = records.length === 0;
    if (datagridList?.length && isEmptyList && !dgRenderInfo[gridItem.key]) {
      replace(datagridList);
    } else if (
      isEmptyList &&
      !gridItem.initEmpty &&
      (datagridList === undefined ||
        datagridList.length === 0 ||
        dgRenderInfo[gridItem.key])
    ) {
      appendRecord();
    }
    updateDgRenderState(gridItem.key);
    const dgConfig = {
      title: gridItem.label,
      initEmpty: gridItem.initEmpty,
      custom: gridItem.validate?.custom,
      properties: gridItem.properties,
    };
    updateDgState({
      key: gridItem.key,
      config: dgConfig,
    });
    return () => {
      dispatch(updateDatagridRecords({ key: gridItem.key, value: [] }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      updateDatagridRecords({
        key: gridItem.key,
        value: records,
        config: { initEmpty: gridItem.initEmpty },
      })
    );
    const hasOpenRecord = records.find((record) => record.status !== "saved");
    allowBtnAction.current = !hasOpenRecord;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const handleAppendRecord = () => {
    const msg =
      "Please save all the record(s) which are currently being edited before performing this action.";
    if (allowBtnAction.current) {
      captureCurrentPosition();
      appendRecord();
      updateHeight();
      scrollToCapturedPosition();
      setTimeout(() => updateHeight(), 50);
    } else dispatch(showNotification({ type: "warning", msg, show: true }));
  };

  if (formConfig.readOnly && (!records || records.length === 0)) {
    return (
      <Typography align="center" sx={{ marginTop: "2rem" }}>
        No information provided.
      </Typography>
    );
  }
  const recordProps = {
    records,
    gridItem,
    updateForm,
    allowBtnAction,
    updateRecord,
    removeRecord,
    appendRecord,
    dgConfigRef,
  };

  return (
    <StyledBox>
      <RecordRenderer {...recordProps} isCategorizedView={isCategorizedView} />
      <Grid container className="addBtnContainer">
        {!formConfig.readOnly && records.length === 0 && (
          <Button onClick={appendRecord} startIcon={<AddIcon />}>
            Add {gridItem.label}
          </Button>
        )}
        {isCategorizedView && showBtnForCategorizedView && (
          <Button
            variant="outlined"
            onClick={handleAppendRecord}
            startIcon={<AddIcon />}
            fullWidth
            className="newRecordBtn"
          >
            Add New Record
          </Button>
        )}
      </Grid>
      <LocalLoader progress={isLoading} />
    </StyledBox>
  );
}
export default DynamicView;
DynamicView.propTypes = {
  gridItem: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    initEmpty: PropTypes.bool,
    validate: PropTypes.shape({
      custom: PropTypes.string,
    }),
  }).isRequired,
  formData: PropTypes.shape({}).isRequired,
  isCategorizedView: PropTypes.bool.isRequired,
};
